import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Registration } from 'src/app/models/registration';
import { InitialSearchResults } from 'src/app/models/initialsSearchResults';
import { SoldPlate } from 'src/app/models/soldPlate';
import { AccountV2Service, FollowedPlate } from 'src/app/services/account-v2-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { RegistrationService } from 'src/app/services/registration-service';
import { SearchService } from 'src/app/services/search-service';
import { SessionService } from 'src/app/services/session-service';
import { SavedPlatesService } from 'src/app/services/saved-plates.service';
import { TrackPlateService } from 'src/app/services/track-plate.service';
import { accountAlertProfile, accountV2Enabled } from 'src/app/site-config';
import { CHAPTERS, Chapter, RELATED_COLLECTIONS, RelatedCard } from './initials-fixtures';
import {
  BandResult,
  PriceExtreme,
  VerifiedSale,
  advertisedRows,
  buildBands,
  buildLookupReg,
  compareRows,
  hasDisplayPrice,
  isAdvertised,
  latestGenuineSale,
} from './initials-feed';
import {
  InitialsFormatKey,
  InitialsSnapshot,
  InitialsSortKey,
  InitialsStatusFilter,
  InitialsStateService,
} from './initials-state.service';
import { excludeRegtransfers } from '../dateless-register/register-feed';

/* =============================================================================
   PlateX — Initials · The PlateX Initials Reference.
   Phase 2 (Gate 2): live five-bucket search over the real plato-search feed.

   Forked from app/pages/dateless-register (the shipped register template),
   NOT from initials-search-page.

   Feed facts this build relies on (verified live, 2026-07-23, HS/JW/A/ABC):
     · searchInitials returns the COMPLETE register range per bucket
       (dateless 9,999 rows for 1-2 letters / 999 for 3; prefix/suffix 1,386;
       current 51), advertised or not; price -1 = not advertised; price in
       pence; seller = listing source.
     · The dateless_letter_first row count IS the register range (contiguous
       from {INI} 1), so the range tile derives from the feed, never hardcoded.

   Rules enforced (build pack §6 + Gate 2 directive):
     · Regtransfers stripped on every ingest (register-feed helper, tested)
     · lowest/highest tiles = client min/max of real display prices
     · range tile labelled register range, never "available now"
     · demand ranking OMITTED (initialspopularity's measure is undocumented;
       §8.2 verdict: not defensible, so it does not ship)
     · format pills carry live advertised counts only, never register sizes
     · no off-market / sold / last-seen / Wanted behaviour (Gate 3)
     · no em/en dashes in published copy
   ========================================================================== */

interface FormatBucket {
  key: InitialsFormatKey;
  style: string;          // feed style token
  label: string;          // pill label (prototype wording)
  note: string;           // format note (prototype wording, initials-neutral)
  allRows: Registration[]; // complete register set, Regtransfers excluded
  rows: Registration[];   // advertised subset of allRows
  rangeCount: number;     // complete feed set size (register range/result set)
  searching: boolean;
  failed: boolean;
  loaded: boolean;
}

@Component({
  selector: 'pla-initials-register',
  templateUrl: './initials-register.component.html',
  styleUrls: ['./initials-register.component.scss'],
})
export class InitialsRegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  public chapters: Chapter[] = CHAPTERS;
  public collections: RelatedCard[] = RELATED_COLLECTIONS;

  public reducedMotion = false;

  /** The applied initials driving every personalised span (default HS, the
      prototype's worked example, until a route param supplies another). */
  public initials = 'HS';
  /** The three input cells; typing state, distinct from the applied value. */
  public cells: string[] = ['H', 'S', ''];

  /* ---- live buckets (Phase 2) ---- */
  public buckets: FormatBucket[] = [
    { key: 'dlf', style: 'dateless_letter_first', label: 'Dateless · Letter First', note: 'Your initials lead the plate, the original arrangement.', allRows: [], rows: [], rangeCount: 0, searching: false, failed: false, loaded: false },
    { key: 'dnf', style: 'dateless_number_first', label: 'Dateless · Number First', note: 'The reversed arrangement, numbers first. Same collectable mark, often a different price.', allRows: [], rows: [], rangeCount: 0, searching: false, failed: false, loaded: false },
    { key: 'cur', style: 'current', label: 'Current Style', note: 'Modern plates that read as your initials.', allRows: [], rows: [], rangeCount: 0, searching: false, failed: false, loaded: false },
    { key: 'pre', style: 'prefix', label: 'Prefix Style', note: 'Prefix plates carrying your initials, the most affordable route in.', allRows: [], rows: [], rangeCount: 0, searching: false, failed: false, loaded: false },
    { key: 'suf', style: 'suffix', label: 'Suffix Style', note: 'Suffix plates ending in your initials. Most are held privately and not advertised.', allRows: [], rows: [], rangeCount: 0, searching: false, failed: false, loaded: false },
  ];
  public selectedKey: InitialsFormatKey = 'dlf';
  public sort: InitialsSortKey = 'price-asc';
  public statusFilter: InitialsStatusFilter = 'all';
  public page = 1;
  public readonly pageSize = 12;
  private feedSeq = 0;
  private pendingRestore: InitialsSnapshot | null = null;

  /* ---- Favourite / Wanted via Account V2 (Phase 3) ---- */
  /** Signed-in user's follows, keyed by canonical registration. */
  private follows = new Map<string, FollowedPlate>();
  public followsLoaded = false;
  /** Registrations with an account request in flight (disables the control). */
  public rowBusy = new Set<string>();
  /** First-use explanation, shown once per session on the first Wanted. */
  public wantedToast = false;
  private wantedToastShown = false;
  private wantedToastTimer: any = null;

  /* ---- verified prior sales (Phase 3 §6; lazy, page-bounded, cached) ---- */
  private saleCache = new Map<string, VerifiedSale | null | 'pending'>();

  /* ---- registration lookup (Phase 3 §5) ---- */
  public lookupTerm = '';

  /* ---- derived profile + budget (recomputed when any bucket lands) ---- */
  public lowest: PriceExtreme | null = null;
  public highest: PriceExtreme | null = null;
  /** Dateless register range from the feed itself (dlf complete set size). */
  public registerRange = 0;
  public bands: BandResult[] = [];

  private routeSub: Subscription | null = null;
  /** Set when apply() triggered the navigation, so the new/updated instance
      scrolls to the profile like the prototype. Survives the component
      recreation that a draft-base to :initials navigation causes. */
  private static appliedNav = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private router: Router,
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private accountService: AccountV2Service,
    private sessionService: SessionService,
    private signupService: QuickSignupService,
    private state: InitialsStateService,
    private savedPlates: SavedPlatesService,
    private trackPlate: TrackPlateService
  ) {}

  /** Hosts without account v2: Favourite and Wanted use the shared Saved Plates /
      Track services, which fall back to the legacy favourite and notify lists there. */
  private readonly legacyLists = !accountV2Enabled;
  /**
   * The heart saves to Saved plates where tracked plates always alert
   * (release builds ask the API for production's legacy alerts, which
   * have no alert-free tracked plate). DEV2 keeps Favourite as a follow.
   */
  private readonly heartSaves = this.legacyLists || accountAlertProfile === 'legacy-alerts';

  ngOnInit(): void {
    this.reducedMotion =
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loadFollows();
    // Back-button restoration (§3.4): transient explorer state + scroll,
    // never URL. Applied only when the snapshot matches the routed initials.
    this.pendingRestore = this.state.takeFresh();
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const token = (params.get('initials') || '').trim();
      if (!token) {
        this.startInitials('HS');
        return;
      }
      if (!/^[a-zA-Z]{1,3}$/.test(token)) {
        // unknown token: fall back to the default on the base route (§3.1)
        this.router.navigate([this.basePath], { replaceUrl: true });
        return;
      }
      this.startInitials(token.toUpperCase());
      if (InitialsRegisterComponent.appliedNav) {
        InitialsRegisterComponent.appliedNav = false;
        // Applying behaves like the prototype: profile scrolled into view.
        // Two-step ladder so the jump lands after the router's own
        // scrollPositionRestoration('top') and initial layout settle.
        [120, 450].forEach((t) => setTimeout(() => this.jumpTo('profile'), t));
      }
    });
  }

  ngAfterViewInit(): void {
    [0, 150, 500, 1200].forEach((t) => setTimeout(() => this.measureShell(), t));
    window.addEventListener('resize', this.measureShellBound, { passive: true });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.measureShellBound);
    if (this.routeSub) this.routeSub.unsubscribe();
    if (this.wantedToastTimer) clearTimeout(this.wantedToastTimer);
    this.state.save({
      initials: this.initials,
      format: this.selectedKey,
      statusFilter: this.statusFilter,
      sort: this.sort,
      page: this.page,
      scrollY: typeof window !== 'undefined' ? Math.round(window.scrollY) : 0,
    });
  }

  /* =============== live feed (Phase 2) =================================== */

  private startInitials(v: string): void {
    const restoring = !!this.pendingRestore && this.pendingRestore.initials === v;
    if (restoring) {
      this.selectedKey = this.pendingRestore!.format;
      this.statusFilter = this.pendingRestore!.statusFilter;
      this.sort = this.pendingRestore!.sort;
      this.page = this.pendingRestore!.page;
      const y = this.pendingRestore!.scrollY;
      [200, 600].forEach((t) => setTimeout(() => window.scrollTo(0, y), t));
    } else if (this.initials !== v) {
      this.selectedKey = 'dlf';
      this.statusFilter = 'all';
      this.sort = 'price-asc';
      this.page = 1;
    }
    this.pendingRestore = null;
    this.initials = v;
    this.cells = [v[0] || '', v[1] || '', v[2] || ''];
    this.fetchAll(v);
  }

  private fetchAll(initials: string): void {
    const seq = ++this.feedSeq;
    this.lowest = null;
    this.highest = null;
    this.registerRange = 0;
    this.bands = [];
    for (const bucket of this.buckets) {
      bucket.searching = true;
      bucket.failed = false;
      bucket.loaded = false;
      bucket.allRows = [];
      bucket.rows = [];
      bucket.rangeCount = 0;
      this.searchService.searchInitials(initials.toLowerCase(), bucket.style, (res: InitialSearchResults) => {
        if (seq !== this.feedSeq) return; // stale response guard
        bucket.searching = false;
        const raw = res && (res as any)[bucket.style];
        if (!raw) {
          bucket.failed = true;
          bucket.loaded = true;
          this.recompute();
          return;
        }
        this.ingestBucket(bucket, raw as Registration[]);
        this.recompute();
      });
    }
  }

  private ingestBucket(bucket: FormatBucket, raw: Registration[]): void {
    // HARD RULE: Regtransfers never reaches a public surface (§4, test).
    bucket.rangeCount = raw.length;
    bucket.allRows = excludeRegtransfers(raw);
    bucket.allRows.forEach((r) => ((r as any).available = (r as any).price >= 0));
    bucket.rows = bucket.allRows.filter(isAdvertised);
    bucket.loaded = true;
    if (bucket.key === this.selectedKey) this.enrichVisible();
  }

  /** Full refetch of every bucket for the current initials. */
  public retryAll(): void {
    this.fetchAll(this.initials);
  }

  /** Retry a single failed bucket without refetching the rest. */
  public retryBucket(bucket: FormatBucket): void {
    const seq = this.feedSeq;
    bucket.searching = true;
    bucket.failed = false;
    this.searchService.searchInitials(this.initials.toLowerCase(), bucket.style, (res: InitialSearchResults) => {
      if (seq !== this.feedSeq) return;
      bucket.searching = false;
      const raw = res && (res as any)[bucket.style];
      if (!raw) {
        bucket.failed = true;
        return;
      }
      this.ingestBucket(bucket, raw as Registration[]);
      this.recompute();
    });
  }

  /** Derived tiles + budget ladder over every loaded bucket (acceptance b). */
  private recompute(): void {
    const all: Registration[] = [];
    for (const b of this.buckets) if (b.loaded && !b.failed) all.push(...b.rows);
    let lowest: PriceExtreme | null = null;
    let highest: PriceExtreme | null = null;
    for (const row of all) {
      if (!hasDisplayPrice(row)) continue;
      const p = (row as any).price as number;
      if (!lowest || p < lowest.pricePence) lowest = { registration: row, pricePence: p };
      if (!highest || p > highest.pricePence) highest = { registration: row, pricePence: p };
    }
    this.lowest = lowest;
    this.highest = highest;
    const dlf = this.buckets.find((b) => b.key === 'dlf');
    this.registerRange = dlf && dlf.loaded && !dlf.failed ? dlf.rangeCount : 0;
    this.bands = buildBands(
      this.buckets
        .filter((b) => b.loaded && !b.failed)
        .map((b) => ({ formatLabel: b.label, rows: b.rows }))
    );
  }

  public get anyLoaded(): boolean {
    return this.buckets.some((b) => b.loaded && !b.failed);
  }

  public get anySearching(): boolean {
    return this.buckets.some((b) => b.searching);
  }

  public get allFailed(): boolean {
    return this.buckets.every((b) => b.loaded && b.failed);
  }

  /* ---- format explorer ---- */

  public get selected(): FormatBucket {
    return this.buckets.find((b) => b.key === this.selectedKey) || this.buckets[0];
  }

  public selectFormat(key: InitialsFormatKey): void {
    if (this.selectedKey === key) return;
    this.selectedKey = key;
    this.page = 1;
    this.enrichVisible();
  }

  /** Status-filtered, sorted view over the selected bucket's register rows.
      Available rows always precede not-advertised rows (initials-feed). */
  public get rowsView(): Registration[] {
    let rows = this.selected.allRows;
    if (this.statusFilter === 'available') rows = rows.filter((r) => isAdvertised(r));
    else if (this.statusFilter === 'unadvertised') rows = rows.filter((r) => !isAdvertised(r));
    return [...rows].sort(compareRows(this.sort));
  }

  public get rowsPage(): Registration[] {
    const start = (this.page - 1) * this.pageSize;
    return this.rowsView.slice(start, start + this.pageSize);
  }

  public get pageCount(): number {
    return Math.max(1, Math.ceil(this.rowsView.length / this.pageSize));
  }

  /** Windowed pager: 1 … (page±2) … last. -1 renders as an ellipsis. */
  public get pagerItems(): number[] {
    const total = this.pageCount;
    if (total <= 9) return Array.from({ length: total }, (_, i) => i + 1);
    const items: number[] = [1];
    const lo = Math.max(2, this.page - 2);
    const hi = Math.min(total - 1, this.page + 2);
    if (lo > 2) items.push(-1);
    for (let p = lo; p <= hi; p++) items.push(p);
    if (hi < total - 1) items.push(-1);
    items.push(total);
    return items;
  }

  public setPage(p: number): void {
    if (p < 0) return; // ellipsis
    this.page = Math.max(1, Math.min(this.pageCount, p));
    this.enrichVisible();
    this.jumpTo('explore');
  }

  public setSort(s: string): void {
    this.sort = s as InitialsSortKey;
    this.page = 1;
    this.enrichVisible();
  }

  public setStatusFilter(f: InitialsStatusFilter): void {
    if (this.statusFilter === f) return;
    this.statusFilter = f;
    this.page = 1;
    this.enrichVisible();
  }

  /* =============== Favourite / Wanted via Account V2 (Phase 3) ===========
     Favourite = follow(reg,'considering') as a bookmark (alerts cleared on a
     NEW follow only; a follow customised elsewhere keeps its alerts).
     Wanted = follow(reg,'notify') + alerts ['available','new_listings'].
     JWT-scoped server side; no public counts; no owner-to-buyer contact. */

  private canonical(reg: string): string {
    return (reg || '').replace(/\s+/g, '').toUpperCase();
  }

  /** True once follows have been fetched during an authenticated session. */
  private followsAuthLoaded = false;

  private loadFollows(): void {
    if (this.legacyLists) this.trackPlate.refresh(false);
    if (this.legacyLists || !this.sessionService.isLoggedIn()) {
      this.followsLoaded = true;
      return;
    }
    this.loadFollowsThen(() => {});
  }

  /** Wanted = intent 'notify'. Any other follow renders as Favourited. */
  public isWanted(row: Registration): boolean {
    if (this.legacyLists) return this.trackPlate.isTracked(row.registration);
    const f = this.follows.get(this.canonical(row.registration));
    return !!f && f.intent === 'notify';
  }

  public isSaved(row: Registration): boolean {
    if (this.heartSaves) return this.savedPlates.isSaved(row.registration);
    const f = this.follows.get(this.canonical(row.registration));
    return !!f && f.intent !== 'notify';
  }

  public isBusy(row: Registration): boolean {
    if (this.legacyLists) return this.savedPlates.isBusy(row.registration) || this.trackPlate.isBusy(row.registration);
    if (this.heartSaves && this.savedPlates.isBusy(row.registration)) return true;
    return this.rowBusy.has(this.canonical(row.registration));
  }

  /** Favourite toggle for an advertised registration (auth-gated). */
  public toggleFavourite(row: Registration): void {
    if (this.heartSaves) {
      this.savedPlates.toggle(row.registration).subscribe();
      return;
    }
    this.signupService.quickSignup(
      `favourite ${this.plateText(row)}`,
      undefined,
      (ok: boolean) => {
        if (!ok) return;
        if (!this.followsAuthLoaded) this.loadFollowsThen(() => this.doToggleFavourite(row));
        else this.doToggleFavourite(row);
      },
      () => {}
    );
  }

  private loadFollowsThen(next: () => void): void {
    this.accountService.getFollowed().subscribe(
      (follows) => {
        this.follows.clear();
        (follows || []).forEach((f) => this.follows.set(this.canonical(f.registration), f));
        this.followsLoaded = true;
        this.followsAuthLoaded = true;
        next();
      },
      () => {
        this.followsLoaded = true;
        next();
      }
    );
  }

  private doToggleFavourite(row: Registration): void {
    const reg = this.canonical(row.registration);
    if (this.rowBusy.has(reg)) return;
    this.rowBusy.add(reg);
    const existing = this.follows.get(reg);
    if (existing && existing.intent !== 'notify') {
      // saved -> remove
      this.accountService.unfollow(reg).subscribe(
        () => { this.follows.delete(reg); this.rowBusy.delete(reg); },
        () => this.rowBusy.delete(reg)
      );
      return;
    }
    const wasFollowed = !!existing;
    this.accountService.follow(reg, 'considering').subscribe(
      (followed) => {
        this.follows.set(reg, followed);
        if (wasFollowed) {
          // DOCUMENTED BEHAVIOUR (Gate 3 review ruling): a plate the user
          // already followed with customised alerts keeps those alerts when
          // Favourited here; only the intent is recorded. The UI must never
          // state that Favourite disables alerts (the legend and the heart's
          // label say "saves" only), because in this path it does not.
          this.rowBusy.delete(reg);
          return;
        }
        // New follow: clear the backend's default alerts to the approved
        // bookmark shape (alertTypes [], frequency none).
        this.accountService.updateFollowAlerts(reg, [], 'none').subscribe(
          (updated) => { this.follows.set(reg, updated); this.rowBusy.delete(reg); },
          () => this.rowBusy.delete(reg)
        );
      },
      () => this.rowBusy.delete(reg)
    );
  }

  /** Wanted toggle for a registration that is not currently advertised. */
  public toggleWanted(row: Registration): void {
    if (this.legacyLists) {
      if (this.trackPlate.isTracked(row.registration)) this.trackPlate.untrack(row.registration).subscribe();
      else this.trackPlate.track(row.registration, this.plateText(row));
      return;
    }
    this.signupService.quickSignup(
      `wanted ${this.plateText(row)}`,
      undefined,
      (ok: boolean) => {
        if (!ok) return;
        if (!this.followsAuthLoaded) this.loadFollowsThen(() => this.doToggleWanted(row));
        else this.doToggleWanted(row);
      },
      () => {}
    );
  }

  private doToggleWanted(row: Registration): void {
    const reg = this.canonical(row.registration);
    if (this.rowBusy.has(reg)) return;
    this.rowBusy.add(reg);
    const existing = this.follows.get(reg);
    if (existing && existing.intent === 'notify') {
      // wanted -> remove
      this.accountService.unfollow(reg).subscribe(
        () => { this.follows.delete(reg); this.rowBusy.delete(reg); },
        () => this.rowBusy.delete(reg)
      );
      return;
    }
    this.accountService.follow(reg, 'notify').subscribe(
      (followed) => {
        // The approved Wanted alert set is definitional, so it is always set,
        // as far as the alert system offers it (production: availability only,
        // which follow() already sets).
        const wanted = ['available', 'new_listings'];
        this.accountService.getCapabilities().pipe(
          switchMap((caps) => wanted.every((t) => caps.plateAlertTypes.includes(t)) && caps.plateFrequencies.includes('instant')
            ? this.accountService.updateFollowAlerts(reg, wanted, 'instant')
            : of(followed))
        ).subscribe(
          (updated) => {
            this.follows.set(reg, updated);
            this.rowBusy.delete(reg);
            this.showWantedToastOnce();
          },
          () => this.rowBusy.delete(reg)
        );
      },
      () => this.rowBusy.delete(reg)
    );
  }

  /** First-use explanation (prototype copy), once per session. */
  private showWantedToastOnce(): void {
    if (this.wantedToastShown) return;
    this.wantedToastShown = true;
    this.wantedToast = true;
    this.wantedToastTimer = setTimeout(() => (this.wantedToast = false), 4600);
  }

  /* =============== verified prior sales (Phase 3 §6) =====================
     Lazy per-page enrichment: at most pageSize history lookups per page
     turn, unadvertised rows only, cached per registration for the session.
     A row claims "Previously sold" ONLY when a genuine sale (the shipped
     Number Ones saleType convention) comes back for it. */

  private enrichVisible(): void {
    setTimeout(() => {
      for (const row of this.rowsPage) {
        if (isAdvertised(row)) continue;
        const reg = this.canonical(row.registration);
        if (this.saleCache.has(reg)) continue;
        this.saleCache.set(reg, 'pending');
        this.registrationService.getPlateHistory(
          reg,
          (sales: SoldPlate[]) => this.saleCache.set(reg, latestGenuineSale(sales)),
          () => this.saleCache.delete(reg)
        );
      }
    });
  }

  /** Verified sale for a row, or null while unknown/absent. */
  public verifiedSale(row: Registration): VerifiedSale | null {
    const hit = this.saleCache.get(this.canonical(row.registration));
    return hit && hit !== 'pending' ? hit : null;
  }

  public saleLine(row: Registration): string {
    const sale = this.verifiedSale(row);
    if (!sale) return '';
    return 'Sold ' + this.money(sale.pricePence) + ' · ' + sale.year;
  }

  /* =============== registration lookup (Phase 3 §5) ====================== */

  public get lookupExamples(): string[] {
    return [this.initials + ' 1', this.initials + ' 999', '9 ' + this.initials];
  }

  /** Prefix box: digits complete the initials; anything with letters is
      treated as a complete registration. Routes to the canonical plate page. */
  public runLookup(term?: string): void {
    const reg = buildLookupReg(this.initials, term !== undefined ? term : this.lookupTerm);
    if (!reg) return;
    this.router.navigate(this.plateLink(reg));
  }

  public statusOf(row: Registration): 'available' | 'unadvertised' {
    return isAdvertised(row) ? 'available' : 'unadvertised';
  }

  /* ---- row / money presentation (register-page conventions) ---- */

  public plateText(row: Registration): string {
    return (row as any).formattedRegistration || row.registration || '';
  }

  public isPOA(row: Registration): boolean {
    const p = (row as any).price;
    return !p || p >= 1000000000; // POA sentinel; feed prices are in pence
  }

  public price(row: Registration): string {
    if (this.isPOA(row)) return 'POA';
    return '£' + Math.round((row as any).price / 100).toLocaleString('en-GB');
  }

  public money(pence: number): string {
    return '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }

  public seller(row: Registration): string {
    return ((row as any).seller || '').toString();
  }

  public plateLink(reg: string): string[] {
    return ['/plate', (reg || '').replace(/\s+/g, '')];
  }

  public trackByRow(_i: number, row: Registration): string {
    return (row as any).id || row.registration;
  }

  public trackByBucket(_i: number, b: FormatBucket): string {
    return b.key;
  }

  public trackByBand(_i: number, r: BandResult): string {
    return r.band.label;
  }

  /* =============== compact initials input (§5) =========================== */

  /** What the cells currently spell; falls back to the applied initials. */
  public get typedInitials(): string {
    return this.cells.join('') || this.initials;
  }

  public onCellInput(i: number, input: HTMLInputElement, next?: HTMLInputElement): void {
    const clean = (input.value || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 1);
    input.value = clean;
    this.cells[i] = clean;
    if (clean && next) next.focus();
  }

  public onCellKeydown(i: number, ev: KeyboardEvent, input: HTMLInputElement, prev?: HTMLInputElement): void {
    if (ev.key === 'Backspace' && !input.value && prev) {
      ev.preventDefault();
      this.cells[i - 1] = '';
      prev.value = '';
      prev.focus();
    }
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.apply();
    }
  }

  public onCellPaste(ev: ClipboardEvent, inputs: HTMLInputElement[]): void {
    ev.preventDefault();
    const text = ((ev.clipboardData && ev.clipboardData.getData('text')) || '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, 3);
    this.cells = [text[0] || '', text[1] || '', text[2] || ''];
    inputs.forEach((el, j) => (el.value = this.cells[j]));
    const target = inputs[Math.min(text.length, 2)];
    if (target) target.focus();
  }

  /** Enter/Explore: write the initials into the URL, the register pattern.
      The paramMap handler personalises the page and scrolls to the profile. */
  public apply(): void {
    const v = this.cells.join('');
    if (!v) return;
    if (v === this.initials && this.router.url.includes('/' + v.toLowerCase())) {
      // Same initials already applied: behave like the prototype and jump.
      this.jumpTo('profile');
      return;
    }
    InitialsRegisterComponent.appliedNav = true;
    this.router.navigate([this.basePath, v.toLowerCase()]);
  }

  /* =============== shell mechanics (register-page pattern) =============== */

  /** The route base this instance lives on: the unindexed draft during
      review, the public reference after the approved swap. */
  private get basePath(): string {
    return this.router.url.startsWith('/initials-draft')
      ? '/initials-draft'
      : '/plates/initials';
  }

  public trackByChapter(_i: number, c: Chapter): string {
    return c.id;
  }

  public trackByCard(_i: number, c: RelatedCard): string {
    return c.title;
  }

  public jumpTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const target = this.el.nativeElement.querySelector('#' + id) as HTMLElement | null;
    if (target) target.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* Shell measurement, the register-page pattern (dateless/names/number-ones). */
  private measureShellBound = (): void => this.measureShellSettled();

  private measureShellSettled(): void {
    requestAnimationFrame(() => this.measureShell());
    setTimeout(() => this.measureShell(), 250);
  }

  private measureShell(): void {
    const shell = (document.querySelector('pla-nav-shell') ||
      document.querySelector('.site-header-container .site-header')) as HTMLElement | null;
    let h = 0;
    if (shell) {
      const pos = getComputedStyle(shell).position;
      h = pos === 'fixed' ? Math.round(shell.getBoundingClientRect().height) : 0;
    }
    this.el.nativeElement.style.setProperty('--shellH', `${h}px`);
  }
}
